import React from 'react';
import { motion } from 'motion/react';
import { X } from 'lucide-react';
import { UserBadge } from '../../core/identity/IdentityRenderer';
import { usePermission } from '../../services/PermissionService';
import { PERMISSION_KICK_MEMBERS } from '../../core/permissions/permissions';

export interface VaultManagementModalsProps {
  settingNicknameForUser: string | null;
  setSettingNicknameForUser: (id: string | null) => void;
  nicknameInput: string;
  setNicknameInput: (input: string) => void;
  setNicknames: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  
  showCreateVaultModal: boolean;
  setShowCreateVaultModal: (show: boolean) => void;
  isEditingVault: boolean;
  newVaultName: string;
  setNewRoomName: (name: string) => void;
  newVaultDesc: string;
  setNewRoomDesc: (desc: string) => void;
  newVaultIcon: string;
  vaultIconInputRef: React.RefObject<HTMLInputElement | null>;
  handleVaultIconUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  newVaultIsPublic: boolean;
  setNewRoomIsPublic: (isPublic: boolean) => void;
  newVaultHasPassword: boolean;
  setNewRoomHasPassword: (hasPassword: boolean) => void;
  newVaultPassword: string;
  setNewRoomPassword: (pwd: string) => void;
  handleCreateVault: () => void;

  showCreateChannelModal: boolean;
  setShowCreateChannelModal: (show: boolean) => void;
  editingChannelId: string | null;
  newChannelName: string;
  setNewChannelName: (name: string) => void;
  newChannelType: 'text' | 'voice';
  setNewChannelType: (type: 'text' | 'voice') => void;
  handleCreateChannel: () => void;

  showMembersModal: boolean;
  setShowMembersModal: (show: boolean) => void;
  vaultMembers: any[];
  activeVault: any;
  currentUser: any;
  getUserFontStyle: (userId: string) => any;
  entityResolver: any;
  migrateLegacyBadge: (badge: any) => any;
  handleKickUserAction: (userId: string) => void;
  handleBanUserAction: (userId: string) => void;
}

export const VaultManagementModals: React.FC<VaultManagementModalsProps> = ({
  settingNicknameForUser,
  setSettingNicknameForUser,
  nicknameInput,
  setNicknameInput,
  setNicknames,
  showCreateVaultModal,
  setShowCreateVaultModal,
  isEditingVault,
  newVaultName,
  setNewRoomName,
  newVaultDesc,
  setNewRoomDesc,
  newVaultIcon,
  vaultIconInputRef,
  handleVaultIconUpload,
  newVaultIsPublic,
  setNewRoomIsPublic,
  newVaultHasPassword,
  setNewRoomHasPassword,
  newVaultPassword,
  setNewRoomPassword,
  handleCreateVault,
  showCreateChannelModal,
  setShowCreateChannelModal,
  editingChannelId,
  newChannelName,
  setNewChannelName,
  newChannelType,
  setNewChannelType,
  handleCreateChannel,
  showMembersModal,
  setShowMembersModal,
  vaultMembers,
  activeVault,
  currentUser,
  getUserFontStyle,
  entityResolver,
  migrateLegacyBadge,
  handleKickUserAction,
  handleBanUserAction,
}) => {
  const canKickOrBan = usePermission(PERMISSION_KICK_MEMBERS, { vaultId: activeVault?.id });
  return (
    <>
      {/* Set Nickname Modal */}
      {settingNicknameForUser && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-[var(--bg-panel)] theme-panel border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h2 className="text-xl font-bold text-white mb-4">Set Nickname</h2>
            <input 
              type="text" 
              value={nicknameInput} 
              onChange={(e) => setNicknameInput(e.target.value)} 
              placeholder="Enter nickname..." 
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white outline-none focus:border-[var(--neon)] mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setNicknames(prev => ({ ...prev, [settingNicknameForUser]: nicknameInput }));
                  setSettingNicknameForUser(null);
                }
              }}
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setSettingNicknameForUser(null)} className="px-4 py-2 rounded-lg text-sm font-bold text-white/50 hover:text-white hover:bg-white/5 transition-colors">Cancel</button>
              <button 
                onClick={() => {
                  setNicknames(prev => ({ ...prev, [settingNicknameForUser]: nicknameInput }));
                  setSettingNicknameForUser(null);
                }} 
                className="px-4 py-2 rounded-lg text-sm font-bold bg-[var(--neon)] text-black hover:opacity-90 transition-opacity"
              >
                Save
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Server Settings / Create Server Modal */}
      {showCreateVaultModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-[var(--bg-panel)] theme-panel border border-white/10 rounded-2xl p-6 w-full max-w-lg shadow-2xl overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white uppercase tracking-widest">{isEditingVault ? 'Server Settings' : 'Create New Server'}</h2>
              <button onClick={() => setShowCreateVaultModal(false)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <input type="text" value={newVaultName} onChange={(e) => setNewRoomName(e.target.value)} placeholder="Server Name" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white outline-none focus:border-[var(--neon)]" />
              <textarea value={newVaultDesc} onChange={(e) => setNewRoomDesc(e.target.value)} placeholder="Server Description" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white outline-none focus:border-[var(--neon)] h-24 resize-none" />
              
              <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg p-3">
                <span className="text-xs font-bold text-white/70">Server Icon</span>
                <div className="flex items-center gap-3">
                  {newVaultIcon && <img src={newVaultIcon} className="w-8 h-8 rounded-lg object-cover" />}
                  <button onClick={() => vaultIconInputRef.current?.click()} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-[10px] font-bold uppercase rounded text-white transition-colors">
                    Upload Icon
                  </button>
                  <input type="file" ref={vaultIconInputRef} className="hidden" accept="image/*" onChange={handleVaultIconUpload} />
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={newVaultIsPublic} onChange={() => setNewRoomIsPublic(true)} className="accent-[var(--neon)]" />
                  <span className="text-xs text-white/70 font-bold">Public Node</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={!newVaultIsPublic} onChange={() => setNewRoomIsPublic(false)} className="accent-[var(--neon)]" />
                  <span className="text-xs text-white/70 font-bold">Private Enclave</span>
                </label>
              </div>

              {!newVaultIsPublic && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={newVaultHasPassword} onChange={(e) => setNewRoomHasPassword(e.target.checked)} className="accent-[var(--neon)]" />
                    <span className="text-xs text-white/70">Require Password</span>
                  </label>
                  {newVaultHasPassword && (
                    <input type="password" value={newVaultPassword} onChange={(e) => setNewRoomPassword(e.target.value)} placeholder="Enter Password" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-sm text-white outline-none focus:border-[var(--neon)]" />
                  )}
                </div>
              )}
              
              <button onClick={handleCreateVault} className="w-full bg-[var(--neon)] text-black font-bold py-3 rounded-lg uppercase tracking-widest mt-4">Initialize Server</button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Create / Edit Channel Modal */}
      {showCreateChannelModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }} 
            className="bg-[var(--bg-panel)] backdrop-blur-xl border border-[var(--stroke)] rounded-2xl p-6 w-full max-w-md shadow-2xl"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-black text-white uppercase tracking-wider">
                {editingChannelId ? 'Edit Channel' : 'Create New Channel'}
              </h2>
              <button 
                onClick={() => setShowCreateChannelModal(false)} 
                className="text-white/50 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-white/40 uppercase tracking-widest mb-2">Channel Name</label>
                <input 
                  type="text" 
                  value={newChannelName} 
                  onChange={(e) => setNewChannelName(e.target.value)} 
                  placeholder="e.g. general" 
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[var(--neon)]" 
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-white/40 uppercase tracking-widest mb-2">Channel Type</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="channelType"
                      checked={newChannelType === 'text'} 
                      onChange={() => setNewChannelType('text')} 
                      className="accent-[var(--neon)]" 
                    />
                    <span className="text-xs text-white/70 font-bold">Text Channel</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="channelType"
                      checked={newChannelType === 'voice'} 
                      onChange={() => setNewChannelType('voice')} 
                      className="accent-[var(--neon)]" 
                    />
                    <span className="text-xs text-white/70 font-bold">Voice Channel</span>
                  </label>
                </div>
              </div>
              
              <button 
                onClick={handleCreateChannel} 
                className="w-full bg-[var(--neon)] text-black font-black py-3 rounded-xl uppercase tracking-widest mt-4 hover:scale-[1.02] active:scale-95 transition-all text-xs"
              >
                {editingChannelId ? 'Save Changes' : 'Initialize Channel'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Node Members List Modal */}
      {showMembersModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }} 
            className="bg-[var(--bg-panel)] backdrop-blur-xl border border-[var(--stroke)] rounded-2xl p-6 w-full max-w-md shadow-2xl flex flex-col max-h-[80vh]"
          >
            <div className="flex justify-between items-center mb-6 shrink-0">
              <h2 className="text-lg font-black text-white uppercase tracking-wider">Node Members</h2>
              <button 
                onClick={() => setShowMembersModal(false)} 
                className="text-white/50 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-grow overflow-y-auto space-y-3 pr-1">
              {vaultMembers.length === 0 ? (
                <div className="text-xs text-white/40 uppercase tracking-widest text-center py-6">No members found</div>
              ) : (
                vaultMembers.map(member => {
                  const isOwner = member.id === activeVault?.created_by;
                  return (
                    <div key={member.id} className="flex items-center justify-between p-2.5 bg-white/5 rounded-xl border border-white/5">
                      <div className="flex items-center gap-3 min-w-0">
                        <img src={member.photo_url || member.avatar} className="w-8 h-8 rounded-full object-cover border border-white/10" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="text-xs font-black text-white uppercase tracking-wide truncate" style={getUserFontStyle(member.id)}>{member.display_name || member.name}</div>
                            <UserBadge customization={migrateLegacyBadge(entityResolver.resolveUser(member.id)?.customization)} />
                          </div>
                          {isOwner && <div className="text-[8px] text-[var(--neon)] font-black uppercase tracking-widest mt-0.5">Creator</div>}
                        </div>
                      </div>
                      
                      {!isOwner && canKickOrBan && (
                        <div className="flex gap-2">
                          <button 
                            onClick={() => handleKickUserAction(member.id)} 
                            className="px-2.5 py-1 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 text-[9px] font-black uppercase tracking-wider rounded border border-orange-500/20 transition-all"
                          >
                            Kick
                          </button>
                          <button 
                            onClick={() => handleBanUserAction(member.id)} 
                            className="px-2.5 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-[9px] font-black uppercase tracking-wider rounded border border-red-500/20 transition-all"
                          >
                            Ban
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default VaultManagementModals;
